import axios from 'axios';
import { API_URL as API } from './utils';

const CACHE_PREFIX = 'evo_cache_';
const CACHE_TTL_MS = 5 * 60 * 1000;

export const getCacheScope = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user'));
    const token = sessionStorage.getItem('token');
    return user?.id && token ? { userId: user.id, companyId: user.company_id || '', token } : null;
  } catch {
    return null;
  }
};

export const isCurrentCacheScope = (scope) => {
  const current = getCacheScope();
  return !!scope && !!current && scope.userId === current.userId
    && scope.companyId === current.companyId && scope.token === current.token;
};

const cacheStorageKey = (key, scope) => `${CACHE_PREFIX}${JSON.stringify([scope.companyId, scope.userId, key])}`;

export const clearPrivateCaches = () => {
  for (const storageName of ['localStorage', 'sessionStorage']) {
    try {
      const storage = window[storageName];
      for (const key of Object.keys(storage)) {
        if (key.startsWith(CACHE_PREFIX) || ['remembered_pass', 'remembered_pass_v2'].includes(key)) storage.removeItem(key);
      }
    } catch {
      // Storage can be unavailable in private browsing.
    }
  }
  if ('caches' in window) {
    window.caches.keys().then((names) => Promise.all(names
      .filter((name) => name.startsWith('evo-taxi-') && name.endsWith('-api'))
      .map((name) => window.caches.delete(name)))).catch(() => {});
  }
};

export const readCache = (key, fallback = null, scope = getCacheScope()) => {
  try {
    if (!isCurrentCacheScope(scope)) return fallback;
    const storageKey = cacheStorageKey(key, scope);
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return fallback;
    const cached = JSON.parse(raw);
    const age = Date.now() - cached.saved_at;
    if (!Number.isFinite(age) || age < 0 || age > CACHE_TTL_MS) {
      sessionStorage.removeItem(storageKey);
      return fallback;
    }
    return cached.data ?? fallback;
  } catch {
    return fallback;
  }
};

export const writeCache = (key, data, scope) => {
  try {
    if (!isCurrentCacheScope(scope)) return;
    sessionStorage.setItem(cacheStorageKey(key, scope), JSON.stringify({
      data,
      saved_at: Date.now()
    }));
  } catch {
    // Cache is best-effort only.
  }
};

export const reportMetric = async (name, startedAt, extra = {}) => {
  try {
    const token = sessionStorage.getItem('token');
    const duration_ms = Math.max(0, Math.round(performance.now() - startedAt));
    await axios.post(`${API}/metrics/performance`, {
      name,
      duration_ms,
      slow_network: duration_ms > 6000,
      ...extra
    }, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      timeout: 5000
    });
  } catch {
    // Diagnostics must never block the app.
  }
};
